<template>
    <div v-if="reservation && metadata.permissions.email_view" class="pt-1">
        <b-button-toolbar class="pt-4">
            <responsive-button
                v-if="userPermissions.can_view_inbox"
                class="mr-2"
                :icon="['fas', 'comments']"
                variant="outline-primary"
                :href="reservation.conversation_url"
                :responsiveness="false"
                :withSpace="false"
            >
                View All
            </responsive-button>          
            <responsive-button
                class="mr-2"
                :icon="['fas', 'envelope']"
                variant="outline-primary"
                @click="showSendEmailModal(null)"
                :responsiveness="false"
                :withSpace="false"
                :disabled="!emailSend"
            >
                Send Message
            </responsive-button>
            <responsive-button
                class="report-templates-buttons mr-2"
                v-for="rt in reportTemplatesFiltered"
                :key="rt.id"
                :icon="['fas', 'envelope']"
                variant="outline-primary"
                @click="showSendEmailModal(rt.id)"
                :responsiveness="false"
                :withSpace="false"
                :disabled="!emailSend"
            >
                Send {{ rt.report_name }}
            </responsive-button>           
            <responsive-button
                class="guest-review-button mr-2"
                v-if="reservation.airbnb_guest_review != null && reservation.airbnb_guest_review.submitted === false"
                :icon="['fab', 'airbnb']"
                variant="outline-primary"
                @click="showAirbnbGuestReviewModal"
                :responsiveness="false"
                :withSpace="false"
                :disabled="!emailSend"
            >
                Create Guest Review
            </responsive-button>
            <responsive-button
                v-if="reservation.airbnb_guest_review != null && reservation.airbnb_guest_review.submitted === true"
                class="guest-review-button"
                :icon="['fab', 'airbnb']"
                variant="outline-primary"
                @click="showSubmittedGuestReview"
                :responsiveness="false"
                :withSpace="false"
                :disabled="!emailSend"
            >
                View Guest Review
            </responsive-button>
            <b-button-group class="more-filters-dropdown">
                <more-filters-dropdown variant="outline-primary">
                        <responsive-button
                            class="report-templates-buttons-dropdown text-left"
                            v-for="rt in reportTemplatesFiltered"
                            :key="rt.id"
                            :icon="['fas', 'envelope']"
                            variant="outline-primary"
                            @click="showSendEmailModal(rt.id)"
                            :responsiveness="false"
                            :withSpace="false"
                            :disabled="!emailSend"
                        >
                            Send {{ rt.report_name }}
                        </responsive-button>
                        <responsive-button
                            v-if="
                                reservation.airbnb_guest_review != null &&
                                reservation.airbnb_guest_review.submitted === false
                            "
                            class="guest-review-button-dropdown text-left"
                            :icon="['fab', 'airbnb']"
                            variant="outline-primary"
                            @click="showAirbnbGuestReviewModal"
                            :responsiveness="false"
                            :withSpace="false"
                            :disabled="!emailSend"
                        >
                            Create Guest Review
                        </responsive-button>
                        <responsive-button
                            v-if="
                                reservation.airbnb_guest_review != null &&
                                reservation.airbnb_guest_review.submitted === true
                            "
                            class="guest-review-button-dropdown text-left"
                            :icon="['fab', 'airbnb']"
                            variant="outline-primary"
                            @click="showSubmittedGuestReview"
                            :responsiveness="false"
                            :withSpace="false"
                            :disabled="!emailSend"
                        >
                            View Guest Review
                        </responsive-button>
                </more-filters-dropdown>
            </b-button-group>
        </b-button-toolbar>
        <grid
            ref="grid"
            class="guest-invoices-grid pt-2"
            :columnDefs="columnDefs"
            :overlayNoRowsTemplate="overlayNoRowsTemplate"
            :rowData="reservation.communication"
            :headerHeight="0"
            :paginationPageSize="6"
            :pagination="true"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :isSearch="false"
            :with-space="false"
            :onCellClicked="onCellClicked"
        >
        </grid>
        <submit-guest-review-modal
            :busy="submitGuestReviewModalLoading"
            @hidden="submitGuestReviewModalHidden"
            :id="submitGuestReviewModalId"
            :is-view="isViewGuestReview"
            :key="submitGuestReviewModalId"
            :review="reservation.airbnb_guest_review"
            @submit-guest-review="submitGuestReview"
            :tags="tags"
        />
        <reservation-send-email-modal
            :key="sendEmailModalKey"
            :modal-id="sendEmailModalId"
            :initial-values="reservation"
            :selected-report-template="selectedReportTemplate"
            :onUpdate="refreshReservation"
        />
        <modal :id="emailBodyModalId" title="Message Content" scrollable size="lg">
            <busy-modal :show="loading"></busy-modal>
            <iframe
                :id="emailBodyIframeId"
                width="100%"
                height="100%"
                frameborder="0"
                scrolling="no"
                marginheight="0"
                marginwidth="0"
                :src="currentEmailBodyUrl"
                @load="emailBodyIframeLoaded"
            ></iframe>
            <template #modal-footer="{ cancel }">
                <div class="w-100">
                    <b-button
                        v-if="!isNil(currentNewWindowUrl)"
                        class="float-left"
                        variant="outline-primary"
                        :href="currentNewWindowUrl"
                        target="_blank"
                    >
                        View In New Window
                    </b-button>
                    <b-button class="float-right ml-2" variant="primary" @click="cancel()"> Close </b-button>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import isNil from 'lodash/isNil';
import { mapGetters } from 'vuex';
import API from '../../../../../api.jsx';
import Grid from '../../../../generic/grid/grid.vue';
import Modal from '../../../../generic/modal/modal.vue';
import BusyModal from '../../../../generic/busy-modal/busy-modal.vue';
import { formatResponseError } from '../../../../../utils/api';
import FormTextarea from '../../../../generic/form-textarea/form-textarea.vue';
import ResponsiveButton from '../../../../generic/responsive-button/responsive-button.vue';
import MoreFiltersDropdown from '../../../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import SubmitGuestReviewModal from '../../../../common/submit-guest-review-modal.vue';
import ReservationSendEmailModal from '../../../../common/reservation-send-email-modal.vue';
import {
    AIRBNB_REVIEW_CATEGORY_TAG_TAGS_ENDPOINT,
    GUEST_RESERVATION_GUEST_REVIEW_ENDPOINT,
    RESERVATION_INVOICE_REPORT_TEMPLATES_ENDPOINT,
} from '../../../../../endpoints';
import { isMediumScreen } from '../../../../../utils/screens';

const SUBMIT_HOSTED_FORM_ACTION = 'SUBMIT_HOSTED_FORM';
const SUBMIT_GUEST_REVIEW_HOSTED_FORM = 'SUBMIT_GUEST_REVIEW_HOSTED_FORM';
const SUBMIT_INVOICE_VARIABLES_HOSTED_FORM = 'SUBMIT_INVOICE_VARIABLES_FORM';

export default {
    name: 'GuestInvoicesCorrespondenceTab',
    components: {
        Grid,
        Modal,
        BusyModal,
        FormTextarea,
        ResponsiveButton,
        MoreFiltersDropdown,
        SubmitGuestReviewModal,
        ReservationSendEmailModal,
    },
    data() {
        return {
            loading: false,
            reportTemplates: [],
            reportTemplatesFiltered: [],
            emailBodyModalId: 'email-body-modal',
            emailBodyIframeId: 'email-body-iframe',
            sendEmailModalId: 'send-email-modal',
            submitGuestReviewModalId: null,
            submitGuestReviewModalLoading: false,
            overlayNoRowsTemplate: `<span style="padding-bottom: 70px;">No emails have been sent</span>`,
            selectedReportTemplate: null,
            currentEmailBodyUrl: null,
            currentNewWindowUrl: null,
            isViewGuestReview: false,
            sendEmailModalKey: 1,
            tags: {},
            isTagsUploaded: false,
        };
    },
    inject: ['showSuccessToast', 'showErrorToast'],
    mounted() {
        this.fetchReportTemplates();
    },
    computed: {
        ...mapGetters('header', ['userPermissions']),
        ...mapGetters('guest', ['metadata', 'reservation']),
        guestId() {
            return parseInt(this.$route.params.id);
        },
        emailSend() {
            return this.metadata.permissions.email_send;
        },
        columnDefs() {
            return [
                {
                    headerName: '',
                    field: 'guest_name',
                    pinned: 'left',
                    minWidth: 175,
                    width: isMediumScreen() ? undefined : 175,
                    autoWidthExclude: this.isMobile,
                },
                {
                    headerName: '',
                    field: 'description',
                    cellRenderer: ({ data }) => {
                        if (
                            [
                                SUBMIT_HOSTED_FORM_ACTION,
                                SUBMIT_GUEST_REVIEW_HOSTED_FORM,
                                SUBMIT_INVOICE_VARIABLES_HOSTED_FORM,
                            ].includes(data.action.sysname)
                        ) {
                            return `<span> ${data.hosted_form_title} was submitted by guest. <a href="${
                                data.download_link
                            }" target="_blank">View Results</a></span>`;
                        }
                        let cell = '<span>';
                        cell += data.template_name;
                        cell += ` ${data.action.name}`;
                        if (data.user != '') cell += ` by ${data.user}`;
                        if (data.sent_to) cell += ` to ${data.sent_to}`;
                        if (data.amount != null) cell += ` Amount: ${data.amount}`;
                        if (data.attachment1 || data.attachment2)
                            cell += `<span class="text-muted" style="font-size: 70%">
                             - ${data.attachment1 ? data.attachment1 : ''} ${
                                data.attachment2 ? data.attachment2 : ''
                            }<span>`;
                        return cell + '</span>';
                    },
                    autoWidthExclude: true,
                },
                {
                    headerName: '',
                    field: 'download_link',
                    type: 'attachmentColumn',
                    sortable: false,
                    maxWidth: 61,
                    minWidth: 61,
                    valueGetter: ({ data }) => {
                        if (!this.userPermissions || !this.userPermissions.can_view_inbox) {
                            return null;
                        }
                        return ![SUBMIT_HOSTED_FORM_ACTION, SUBMIT_GUEST_REVIEW_HOSTED_FORM].includes(
                            data.action.sysname
                        )
                            ? data.download_link
                            : null;
                    },
                },
                {
                    headerName: '',
                    field: 'chat_message_url',
                    type: 'linkColumn',
                    sortable: false,
                    maxWidth: 61,
                    minWidth: 61,
                    cellRendererParams: { icon: ['fas', 'comments'], target: '_self' },
                    valueGetter: ({ data }) => {
                        if (!this.userPermissions || !this.userPermissions.can_view_inbox) {
                            return null;
                        }
                        return data.chat_message_url;
                    },
                },
                {
                    headerName: '',
                    field: 'acted',
                    type: 'dateTimeColumn',
                    cellRendererParams: { isBadge: true, badgeVariant: 'info' },
                },
            ];
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
        isVertical() {
            return window.innerWidth < 880;
        },
    },
    methods: {
        isNil,
        refreshReservation() {
            this.sendEmailModalKey++;
            this.$emit('refreshReservations');
        },
        onCellClicked({ column, data }) {
            if (
                ['guest_name', 'description', 'acted'].includes(column.colId) &&
                ![SUBMIT_HOSTED_FORM_ACTION, SUBMIT_GUEST_REVIEW_HOSTED_FORM].includes(data.action.sysname)
            ) {
                this.loading = true;
                this.$bvModal.show(this.emailBodyModalId);
                this.currentEmailBodyUrl = `/system/reservations/get-email-text/${data.id}`;
                this.currentNewWindowUrl = data.chat_message_url;
            }
        },
        emailBodyIframeLoaded() {
            this.loading = false;
            const iframe = document.getElementById(this.emailBodyIframeId);
            iframe.style.height = iframe.contentWindow.document.body.offsetHeight + 'px';
            const sendForm = iframe.contentWindow.document.querySelector('#sendform');
            if (sendForm) {
                sendForm.style.overflow = 'auto';
            }
        },
        showSendEmailModal(id) {
            this.selectedReportTemplate = this.reportTemplates.find((item) => item.id === id) || { id: null };
            this.$bvModal.show(this.sendEmailModalId);
        },
        async showAirbnbGuestReviewModal() {
            this.isViewGuestReview = false;
            if (this.isTagsUploaded) {
                this.submitGuestReviewModalId = `submit-guest-review-modal-id-${
                    this.reservation.airbnb_guest_review.id
                }`;
                this.$nextTick(() => {
                    this.$bvModal.show(this.submitGuestReviewModalId);
                });
            } else {
                this.$emit('busy', true);
                try {
                    const { data } = await API.get(AIRBNB_REVIEW_CATEGORY_TAG_TAGS_ENDPOINT());
                    this.tags = data;
                    this.isTagsUploaded = true;
                    this.submitGuestReviewModalId = `submit-guest-review-modal-id-${
                        this.reservation.airbnb_guest_review.id
                    }`;
                    this.$nextTick(() => {
                        this.$bvModal.show(this.submitGuestReviewModalId);
                    });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.showErrorToast({
                        text: generic || 'Could not load tags.',
                    });
                } finally {
                    this.$emit('busy', false);
                }
            }
        },
        showSubmittedGuestReview() {
            this.submitGuestReviewModalId = `submit-guest-review-modal-id-${this.reservation.airbnb_guest_review.id}`;
            this.isViewGuestReview = true;
            this.$nextTick(() => {
                this.$bvModal.show(this.submitGuestReviewModalId);
            });
        },
        submitGuestReviewModalHidden() {
            this.submitGuestReviewModalId = null;
        },
        async submitGuestReview(review_data) {
            this.submitGuestReviewModalLoading = true;
            try {
                await API.post(GUEST_RESERVATION_GUEST_REVIEW_ENDPOINT(this.reservation.id, this.guestId), {
                    public_review: review_data.publicReview,
                    private_feedback: review_data.privateFeedback,
                    cleanliness: review_data.cleanliness,
                    communication: review_data.communication,
                    respect_house_rules: review_data.respectHouseRules,
                    is_reviewee_recommended: review_data.isRevieweeRecommended,
                    tags: review_data.tags,
                });
                this.$bvModal.hide(this.submitGuestReviewModalId);
                this.refreshReservation();
                this.showSuccessToast({
                    text: 'Guest review successfully submitted.',
                });
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.showErrorToast({
                    text: generic || 'Could not submit guest review.',
                });
            } finally {
                this.submitGuestReviewModalLoading = false;
            }
        },
        async fetchReportTemplates() {
            try {
                const response = await API.get(RESERVATION_INVOICE_REPORT_TEMPLATES_ENDPOINT());
                if (!this.reservation.airbnb_messaging_api && !this.reservation.vrbo_messaging_api) {
                    this.reportTemplates = response.data;
                    this.reportTemplatesFiltered = this.reportTemplates.filter((obj) => {
                        return obj.is_default_confirmation || obj.is_default_invoice || obj.id === null || obj.shortcut;
                    });
                } else this.reportTemplatesFiltered = this.reportTemplates;
            } catch (err) {
                this.showErrorToast({ text: 'Could not load report template.' });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../guest-invoices-grid.scss';

button.guest-review-button,
button.report-templates-buttons {
    @media (max-width: 1000px) {
        display: none !important;
    }
}

.more-filters-dropdown,
button.guest-review-button-dropdown,
button.report-templates-buttons-dropdown {
    @media (min-width: 1000px) {
        display: none !important;
    }
}
</style>
